import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';
import ErrorBoundary from './components/ErrorBoundary';
import { NotificationCenter } from './components/NotificationCenter';
import { useDeepLinkListener } from './hooks/useDeepLinkListener';

// Static: Critical path (login)
import LoginPage from './pages/auth/LoginPage';
import AuthLayout from './layouts/AuthLayout';

// Lazy: Auth pages (non-login)
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const ChangePasswordPage = lazy(() => import('./pages/auth/ChangePasswordPage'));

// Lazy: Main layout
const MainLayout = lazy(() => import('./layouts/MainLayout'));

// Lazy: All protected pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const NuevoMovimientoPage = lazy(() => import('./pages/NuevoMovimientoPage'));
const MovimientosPage = lazy(() => import('./pages/MovimientosPage'));
const TarjetasPage = lazy(() => import('./pages/TarjetasPage'));
const GastosFijosPage = lazy(() => import('./pages/GastosFijosPage'));
const MetasPage = lazy(() => import('./pages/MetasPage'));
const PresupuestosPage = lazy(() => import('./pages/PresupuestosPage'));
const InversionesPage = lazy(() => import('./pages/InversionesPage'));
const InversionDetallePage = lazy(() => import('./pages/InversionDetallePage'));
const PlanesAhorroPage = lazy(() => import('./pages/PlanesAhorroPage'));
const PlanAhorroDetallePage = lazy(() => import('./pages/PlanAhorroDetallePage'));
const CuentasPage = lazy(() => import('./pages/CuentasPage'));
const PatrimonioPage = lazy(() => import('./pages/PatrimonioPage'));
const DeudasPage = lazy(() => import('./pages/DeudasPage'));
const GastosCompartidosPage = lazy(() => import('./pages/GastosCompartidosPage'));
const PatronesPage = lazy(() => import('./pages/PatronesPage'));
const PanoramaPage = lazy(() => import('./pages/PanoramaPage'));
const RadiografiaPage = lazy(() => import('./pages/RadiografiaPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const ImportPage = lazy(() => import('./pages/ImportPage'));
const HogarSettingsPage = lazy(() => import('./pages/HogarSettingsPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage'));

// Loading spinner component
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);

  // Wait for hydration before checking auth
  if (!hasHydrated) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);

  // Wait for hydration before checking auth
  if (!hasHydrated) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  useDeepLinkListener();
  return (
    <ErrorBoundary>
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
      </Route>

      {/* Protected routes */}
      <Route element={<MainLayout />}>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/movimientos"
          element={
            <ProtectedRoute>
              <MovimientosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tarjetas"
          element={
            <ProtectedRoute>
              <TarjetasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gastos-fijos"
          element={
            <ProtectedRoute>
              <GastosFijosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/metas"
          element={
            <ProtectedRoute>
              <MetasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/presupuestos"
          element={
            <ProtectedRoute>
              <PresupuestosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inversiones"
          element={
            <ProtectedRoute>
              <InversionesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inversiones/:id"
          element={
            <ProtectedRoute>
              <InversionDetallePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planes-ahorro"
          element={
            <ProtectedRoute>
              <PlanesAhorroPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planes-ahorro/:id"
          element={
            <ProtectedRoute>
              <PlanAhorroDetallePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cuentas"
          element={
            <ProtectedRoute>
              <CuentasPage />
            </ProtectedRoute>
          }
        />
        {/* C19 PR-7 (B2) / C20 PR-4: Patrimonio neto registrado */}
        <Route
          path="/patrimonio"
          element={
            <ProtectedRoute>
              <PatrimonioPage />
            </ProtectedRoute>
          }
        />
        {/* P1: FEATURE-010 - Deudas */}
        <Route
          path="/deudas"
          element={
            <ProtectedRoute>
              <DeudasPage />
            </ProtectedRoute>
          }
        />
        {/* LEGACY-004 & LEGACY-005: Gastos Compartidos y Balance */}
        <Route
          path="/gastos-compartidos"
          element={
            <ProtectedRoute>
              <GastosCompartidosPage />
            </ProtectedRoute>
          }
        />
        {/* LEGACY-003: Detección de Patrones de Gasto */}
        <Route
          path="/patrones"
          element={
            <ProtectedRoute>
              <PatronesPage />
            </ProtectedRoute>
          }
        />
        {/* INSIGHT-001: Radiografia Financiera (Estado de Resultados Personal) */}
        <Route
          path="/radiografia"
          element={
            <ProtectedRoute>
              <RadiografiaPage />
            </ProtectedRoute>
          }
        />
        {/* INSIGHT-002: Panorama (Forecast de Flujo de Efectivo) */}
        <Route
          path="/panorama"
          element={
            <ProtectedRoute>
              <PanoramaPage />
            </ProtectedRoute>
          }
        />
        {/* Workstream A Routes */}
        <Route
          path="/import"
          element={
            <ProtectedRoute>
              <ImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hogar"
          element={
            <ProtectedRoute>
              <HogarSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:sessionId"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        {/* AUTH-001: Cambio de contraseña */}
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePasswordPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Onboarding - Sin layout (pantalla completa) */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Accept Invitation - Public route */}
      <Route path="/join/:token" element={<AcceptInvitationPage />} />
      {/* C23 INV1: alias para emails ya enviados con la URL antigua
          `/accept-invite/:token` (antes caian en el catch-all -> `/`). */}
      <Route path="/accept-invite/:token" element={<AcceptInvitationPage />} />

      {/* Captura Rapida - Sin layout (pantalla completa) */}
      <Route
        path="/nuevo"
        element={
          <ProtectedRoute>
            <NuevoMovimientoPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    {/* C25 (tri-audit, PR-3): NotificationCenter existía en el store/componente
        pero nunca estaba montado en el árbol de React — cualquier llamada a
        useNotificationActions().error()/.success()/.warning() (incluida la ya
        existente en TarjetasPage.tsx) actualizaba el store sin que nada la
        renderizara. Montaje único a nivel raíz para que los toasts sean
        visibles en toda la app, no solo en /chat. */}
    <NotificationCenter />
    </ErrorBoundary>
  );
}

export default App;
